import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  totalUnread,
  type HydratedStory,
  type ResolvedPhoneShell,
  type ThreadView,
} from 'magical-trails';

import { guardTopState } from '../../hooks/useLeaveTrailGuard';
import { announceArrival } from './arrivalSound';
import type { HomeApp } from './PhoneHome';
import {
  phonePaths,
  phoneScreenFor,
  trailBaseUrl,
  type PhoneScreen,
} from './phoneRoutes';
import usePhoneMessages, { type PhoneArrival } from './usePhoneMessages';

export interface PhoneController {
  shell: ResolvedPhoneShell;
  story: HydratedStory;
  base: string;
  screen: PhoneScreen;
  /** A phone screen is covering the trail (so the trail should be inert). */
  covering: boolean;
  threads: ThreadView[];
  apps: HomeApp[];
  arrival: PhoneArrival | null;
  dismissArrival: () => void;
  /** Go somewhere inside the phone; back returns from it. */
  open: (path: string) => void;
  /** Leave the current screen for its parent. */
  up: (parent: string) => void;
  showOnMap: (pointCode: string) => void;
}

interface NavState {
  phoneNav?: boolean;
}

/**
 * Everything the phone shell's screens share: where the player is, what has
 * arrived, and how to move about. Always called (hooks can't be conditional)
 * and returns null for a story that uses the standard map player.
 */
export function usePhoneController(
  shell: ResolvedPhoneShell | null,
  story: HydratedStory | undefined,
  options: { openMenu: () => void; selectPoint: (code: string) => void }
): PhoneController | null {
  const history = useHistory();
  const location = useLocation();
  const base = trailBaseUrl(location.pathname);
  const screen = phoneScreenFor(location.pathname);
  const openThreadKey = screen.screen === 'thread' ? screen.threadKey : null;

  const { threads, arrival, dismissArrival } = usePhoneMessages(
    shell ? story : undefined,
    openThreadKey
  );

  const open = useCallback(
    (path: string) => history.push(path, { phoneNav: true } as NavState),
    [history]
  );

  // Arrived here from inside the phone: step back, so the history stack
  // stays as deep as the player actually is. Arrived by a link or a reload:
  // there is no such step to take, so swap this entry for its parent.
  const cameFromPhone = (location.state as NavState | null)?.phoneNav === true;
  const up = useCallback(
    (parent: string) => {
      if (cameFromPhone) history.goBack();
      else history.replace(parent);
    },
    [cameFromPhone, history]
  );

  const { openMenu, selectPoint } = options;
  const layout = shell?.layout;
  const showOnMap = useCallback(
    (pointCode: string) => {
      selectPoint(pointCode);
      if (layout === 'home') open(phonePaths.map(base));
      // Dock: the map IS the base route. A bare push there would look to the
      // leave guard like a fresh arrival, and it would plant another spare
      // entry under it: back from the map would then ask "Leave the trail?"
      // instead of returning to the thread. Marked as the guard's own top
      // entry, it is left alone and back works.
      else history.push(base, { ...guardTopState(), phoneNav: true });
    },
    [selectPoint, layout, open, base, history]
  );

  const unread = totalUnread(threads);
  const apps = useMemo<HomeApp[]>(() => {
    if (!shell) return [];
    const listed = shell.apps
      // In the dock layout the map IS the main screen, not an app.
      .filter((app) => !(shell.layout === 'dock' && app.id === 'map'))
      .map((app): HomeApp => {
        switch (app.id) {
          case 'messages':
            return {
              id: 'messages',
              label: app.label,
              badge: unread || undefined,
              onOpen: () => open(phonePaths.messages(base)),
            };
          case 'map':
            return {
              id: 'map',
              label: app.label,
              onOpen: () => open(phonePaths.map(base)),
            };
          case 'scanner':
            return {
              id: 'scanner',
              label: app.label,
              onOpen: () => history.push(phonePaths.scan(base)),
            };
          case 'codeEntry':
            return {
              id: 'codeEntry',
              label: app.label,
              onOpen: () => history.push(phonePaths.codeEntry(base)),
            };
        }
      });
    return [...listed, { id: 'menu', label: 'Settings', onOpen: openMenu }];
  }, [shell, unread, open, base, history, openMenu]);

  // Chime once per arrival.
  const announcedRef = useRef<string | null>(null);
  const theme = shell?.theme;
  useEffect(() => {
    if (!arrival || !theme || announcedRef.current === arrival.id) return;
    announcedRef.current = arrival.id;
    announceArrival(theme);
  }, [arrival, theme]);

  if (!shell || !story) return null;
  const covering =
    screen.screen === 'messages' ||
    screen.screen === 'thread' ||
    (screen.screen === 'base' && shell.layout === 'home');
  return {
    shell,
    story,
    base,
    screen,
    covering,
    threads,
    apps,
    arrival,
    dismissArrival,
    open,
    up,
    showOnMap,
  };
}

export default usePhoneController;
